import type { WhatsAppAiReplyShowResponse } from '@jobilla/nexus-sdk'
import { SparklesIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type WhatsAppAiReviewState = WhatsAppAiReplyShowResponse

export interface WhatsAppAiReviewPanelProps {
	state: WhatsAppAiReviewState | null | undefined
}

const BULLET = /^[-•]\s+/

export function WhatsAppAiReviewPanel({ state }: WhatsAppAiReviewPanelProps) {
	const { t } = useTranslation('common')

	if (!state?.status) {
		return null
	}

	return (
		<div className="border-t border-grey-100 bg-brand-50/40 px-4 py-3">
			<div className="flex items-center gap-2">
				<SparklesIcon className="size-4 text-brand-600" />
				<span className="text-xs font-semibold text-brand-700">{t('whatsapp.aiReview.title')}</span>
				<span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
					{t(`whatsapp.aiReview.status.${state.status}`)}
				</span>
			</div>
			<WhatsAppAiSummaryCard summary={state.summary} />
		</div>
	)
}

function WhatsAppAiSummaryCard({ summary }: { summary?: string | null }) {
	const { t } = useTranslation('common')
	const lines = (summary ?? '').split('\n').map((line) => line.trim()).filter(Boolean)

	if (lines.length === 0) {
		return null
	}

	const isList = lines.every((line) => BULLET.test(line))

	return (
		<section className="relative mt-2.5 overflow-hidden rounded-md bg-white/80 py-2.5 pl-4 pr-3 ring-1 ring-brand-100">
			<span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-brand-400 to-brand-600" />
			<h3 className="flex items-center gap-1.5 text-xs font-medium text-brand-700">
				<SparklesIcon className="size-3.5" />
				{t('whatsapp.aiReview.summary')}
			</h3>
			<div className="mt-1.5 max-h-40 overflow-y-auto text-sm leading-relaxed text-grey-800">
				{isList
					? (
						<ul className="space-y-1">
							{lines.map((line, index) => (
								<li key={`${index}-${line}`} className="flex gap-2">
									<span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-400" />
									<span>{line.replace(BULLET, '')}</span>
								</li>
							))}
						</ul>
					)
					: <p className="whitespace-pre-line">{lines.join('\n')}</p>}
			</div>
		</section>
	)
}
